/**
 * Company branding logo for client-side PDF exports (Snags, Activity, etc.).
 * jsPDF origin is top-left: we stamp top-right (page 1 only for Activity/Snags; use opts.pages).
 * Team sign-off sheets use a different pipeline (server pdf-lib, default bottom-right below the grid).
 * Loads via canvas; PNG output preserves transparency.
 */

const PDF_EDGE = 40;
const MAX_LOGO_W_PT = 72;

/**
 * @param {string} logoUrl
 * @returns {Promise<{ dataUrl: string; wPt: number; hPt: number; format: 'PNG' | 'JPEG' } | null>}
 */
export async function loadLogoDataUrlForPdf(logoUrl) {
  if (!logoUrl || typeof logoUrl !== 'string') return null;
  return new Promise((resolve) => {
    const go = (src) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const maxPx = 480;
        let w = img.naturalWidth || img.width;
        let h = img.naturalHeight || img.height;
        if (!w || !h) {
          resolve(null);
          return;
        }
        const scale = Math.min(1, maxPx / Math.max(w, h));
        w = Math.round(w * scale);
        h = Math.round(h * scale);
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }
        try {
          ctx.drawImage(img, 0, 0, w, h);
        } catch {
          resolve(null);
          return;
        }
        let dataUrl;
        let format;
        try {
          dataUrl = canvas.toDataURL('image/png');
          format = 'PNG';
        } catch {
          try {
            dataUrl = canvas.toDataURL('image/jpeg', 0.92);
            format = 'JPEG';
          } catch {
            resolve(null);
            return;
          }
        }
        const wPt = Math.min(MAX_LOGO_W_PT, w * 0.75);
        const hPt = (h / w) * wPt;
        resolve({ dataUrl, wPt, hPt, format });
      };
      img.onerror = () => resolve(null);
      img.src = src;
    };

    if (logoUrl.startsWith('data:')) {
      go(logoUrl);
      return;
    }
    fetch(logoUrl, { mode: 'cors' })
      .then((r) => (r.ok ? r.blob() : Promise.reject()))
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        go(objectUrl);
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
      })
      .catch(() => go(logoUrl));
  });
}

/**
 * Letterhead-style mark: top-right on each page (not the same geometry as team sign-off).
 * @param {import('jspdf').jsPDF} pdf
 * @param {{ dataUrl: string; wPt: number; hPt: number; format: 'PNG' | 'JPEG' }} logo
 * @param {{ pages?: 'each' | 'first' }} [opts]
 */
export function stampCompanyLogoOnPages(pdf, logo, opts = {}) {
  const { pages = 'each' } = opts;
  const n = pdf.getNumberOfPages();
  const pageW = pdf.internal.pageSize.getWidth();
  const toDraw = pages === 'first' ? [1] : Array.from({ length: n }, (_, i) => i + 1);
  for (const i of toDraw) {
    if (i < 1 || i > n) continue;
    pdf.setPage(i);
    const x = pageW - PDF_EDGE - logo.wPt;
    const y = PDF_EDGE;
    try {
      pdf.addImage(logo.dataUrl, logo.format, x, y, logo.wPt, logo.hPt, undefined, 'FAST');
    } catch {
      /* ignore */
    }
  }
}
